import { wait } from './wait'

export interface WithRetryOptions {
  /** Total tries, including the first. */
  attempts: number
  /** Fixed pause between one failed try and the next. */
  delayMs: number
  /** Message for the wrapped error when a non-`Error` is thrown. */
  fallbackMessage: string
}

/**
 * Runs `operation` until it succeeds, giving up after `attempts` tries.
 *
 * The last error is re-thrown as-is when it is an `Error`, so callers keep the
 * original failure — an `ApiError`'s status and code survive, which is what
 * lets a caller tell "expired" apart from "network down". A non-`Error` throw
 * has no usable message, so it is wrapped in one carrying `fallbackMessage`.
 *
 * @throws The last error, or `Error(fallbackMessage)` when it was not an `Error`.
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  { attempts, delayMs, fallbackMessage }: WithRetryOptions
): Promise<T> {
  let lastError: unknown

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await operation()
    } catch (error) {
      lastError = error
      if (attempt < attempts) await wait(delayMs)
    }
  }

  throw lastError instanceof Error ? lastError : new Error(fallbackMessage)
}
